<script>
import { createWorkShare, getWorkShareDetail, getWorkShareOptions, updateWorkShare } from '@/api/workShare.js'
import { uploadAttachment } from '@/api/attachment.js'
import RichTextEditor from '@/components/RichTextEditor.vue'

const MAX_FILE_SIZE = 100 * 1024 * 1024

export default {
  name: 'WorkShareFormPage',
  components: { RichTextEditor },
  data() {
    return {
      loading: false,
      saving: false,
      uploading: false,
      memberLoading: false,
      memberSearchTimer: null,
      form: {
        postType: 'GENERAL',
        priorityType: 'NORMAL',
        workStatus: 'OPEN',
        boardTitle: '',
        boardText: '',
        dueDate: '',
        dueTime: '00:00',
        referenceUserCode: '',
        targetBranchCodes: [],
        targetAdminCodes: [],
        attachments: [],
      },
      selectedFiles: [],
      options: {
        branches: [],
        admins: [],
        members: [],
      },
      postTypes: [
        { title: '일반', value: 'GENERAL' },
        { title: '요청', value: 'REQUEST' },
        { title: '공지', value: 'NOTICE' },
        { title: '이슈', value: 'ISSUE' },
      ],
      statuses: [
        { title: '대기', value: 'OPEN' },
        { title: '확인', value: 'CONFIRMED' },
        { title: '완료', value: 'DONE' },
        { title: '보류', value: 'HOLD' },
      ],
      priorities: [
        { title: '보통', value: 'NORMAL' },
        { title: '중요', value: 'IMPORTANT' },
        { title: '긴급', value: 'URGENT' },
      ],
    }
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.workSharePostCode)
    },
    pageTitle() {
      return this.isEdit ? '업무공유 게시글 수정' : '업무공유 게시글 등록'
    },
    branchItems() {
      return this.options.branches.map(v => ({ title: `${v.name}${v.subName ? ` · ${v.subName}` : ''}`, value: v.code }))
    },
    adminItems() {
      return this.options.admins.map(v => ({ title: `${v.name}${v.branchName ? ` · ${v.branchName}` : ''}`, value: v.code }))
    },
    memberItems() {
      return [{ title: '참조 회원 없음', value: '' }].concat(
        this.options.members.map(v => ({ title: `${v.name}${v.subName ? ` · ${v.subName}` : ''}`, value: v.code })),
      )
    },
    editorUploadPath() {
      const now = new Date()
      const yyyy = now.getFullYear()
      const mm = String(now.getMonth() + 1).padStart(2, '0')
      const dd = String(now.getDate()).padStart(2, '0')
      return `goldnara/work-share-editor/${yyyy}/${mm}/${dd}/`
    },
  },
  async mounted() {
    await this.loadOptions()
    if (this.isEdit) await this.loadDetail()
  },
  beforeUnmount() {
    window.clearTimeout(this.memberSearchTimer)
  },
  methods: {
    async loadOptions() {
      const res = await getWorkShareOptions()
      if (res.data?.code === 0) {
        this.options = res.data.data || this.options
      }
    },
    async loadMemberOptions(keyword = '') {
      this.memberLoading = true
      try {
        const res = await getWorkShareOptions({ memberKeyword: keyword })
        if (res.data?.code === 0) {
          const data = res.data.data || {}
          this.options.members = data.members || []
        }
      } finally {
        this.memberLoading = false
      }
    },
    searchMembers(keyword) {
      window.clearTimeout(this.memberSearchTimer)
      this.memberSearchTimer = window.setTimeout(() => {
        this.loadMemberOptions(keyword || '')
      }, 250)
    },
    async loadDetail() {
      this.loading = true
      try {
        const res = await getWorkShareDetail(this.$route.params.workSharePostCode)
        if (res.data?.code === 0) {
          const data = res.data.data || {}
          const post = data.post || {}
          this.form = {
            postType: post.postType || 'GENERAL',
            priorityType: post.priorityType || 'NORMAL',
            workStatus: post.workStatus || 'OPEN',
            boardTitle: post.boardTitle || '',
            boardText: post.boardText || '',
            ...this.toDueInputs(post.dueDt),
            referenceUserCode: post.referenceUserCode || '',
            targetBranchCodes: (data.branchTargets || []).map(v => v.branchCode).filter(Boolean),
            targetAdminCodes: (data.adminTargets || []).map(v => v.adminUserCode).filter(Boolean),
            attachments: data.attachments || [],
          }
          this.ensureReferenceMember(post)
        }
      } finally {
        this.loading = false
      }
    },
    ensureReferenceMember(post) {
      if (!post?.referenceUserCode) return
      if (this.options.members.some(v => v.code === post.referenceUserCode)) return
      this.options.members.unshift({
        code: post.referenceUserCode,
        name: post.referenceUserName || post.referenceUserEmail || post.referenceUserCode,
        subName: post.referenceUserEmail || '',
      })
    },
    toDueInputs(value) {
      if (!value) return { dueDate: '', dueTime: '00:00' }
      const normalized = String(value).replace('T', ' ')
      return {
        dueDate: normalized.substring(0, 10),
        dueTime: normalized.substring(11, 16) || '00:00',
      }
    },
    toApiDueDt() {
      if (!this.form.dueDate) return null
      return `${this.form.dueDate}T${this.form.dueTime || '00:00'}:00`
    },
    async uploadFiles() {
      if (!this.selectedFiles?.length) return []
      this.uploading = true
      try {
        const now = new Date()
        const yyyy = now.getFullYear()
        const mm = String(now.getMonth() + 1).padStart(2, '0')
        const dd = String(now.getDate()).padStart(2, '0')
        const path = `goldnara/work-share/${yyyy}/${mm}/${dd}/`
        const uploaded = []
        for (const file of this.selectedFiles) {
          if (file.size > MAX_FILE_SIZE) {
            window.alert(`${file.name} 파일은 100MB를 초과할 수 없습니다.`)
            continue
          }
          const res = await uploadAttachment(path, file)
          if (res.data?.code === 0) {
            uploaded.push({
              originalFileName: file.name,
              storageKey: res.data.objectKey || res.data.path,
              fileUrl: res.data.url || '',
              mimeType: file.type || 'application/octet-stream',
              fileSize: file.size,
            })
          }
        }
        return uploaded
      } finally {
        this.uploading = false
      }
    },
    async save() {
      if (!this.form.boardTitle.trim()) {
        window.alert('제목을 입력해주세요.')
        return
      }
      if (!this.stripHtml(this.form.boardText).trim()) {
        window.alert('내용을 입력해주세요.')
        return
      }
      if (!this.form.targetBranchCodes.length && !this.form.targetAdminCodes.length) {
        window.alert('대상 지점 또는 대상 담당자를 1개 이상 선택해주세요.')
        return
      }
      this.saving = true
      try {
        const uploaded = await this.uploadFiles()
        const { dueDate, dueTime, ...formPayload } = this.form
        const payload = {
          ...formPayload,
          workStatus: this.isEdit ? formPayload.workStatus : 'OPEN',
          dueDt: this.toApiDueDt(),
          attachments: [...(this.form.attachments || []), ...uploaded].map(v => ({
            originalFileName: v.originalFileName,
            storageKey: v.storageKey,
            fileUrl: v.fileUrl,
            mimeType: v.mimeType,
            fileSize: v.fileSize,
          })),
        }
        const res = this.isEdit
          ? await updateWorkShare(this.$route.params.workSharePostCode, payload)
          : await createWorkShare(payload)
        if (res.data?.code !== 0) {
          window.alert(res.data?.message || '저장에 실패했습니다.')
          return
        }
        this.$router.push(`/work-share/${res.data.data?.post?.workSharePostCode || this.$route.params.workSharePostCode}`)
      } finally {
        this.saving = false
      }
    },
    removeAttachment(index) {
      this.form.attachments.splice(index, 1)
    },
    addAttachmentFile(event) {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) return
      if (file.size > MAX_FILE_SIZE) {
        window.alert(`${file.name} 파일은 100MB를 초과할 수 없습니다.`)
        return
      }
      this.selectedFiles.push(file)
    },
    removeSelectedFile(index) {
      this.selectedFiles.splice(index, 1)
    },
    formatFileSize(size) {
      if (!size) return '-'
      if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
      return `${(size / 1024 / 1024).toFixed(1)} MB`
    },
    stripHtml(value) {
      return String(value || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ')
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-4">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">{{ pageTitle }}</h1>
        <div class="text-body-2 text-grey">공유할 업무 내용, 대상 지점 및 담당자를 입력합니다.</div>
      </div>
      <v-btn variant="text" to="/work-share">목록</v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <v-card rounded="lg" elevation="1" class="pa-5">
      <v-row>
        <v-col cols="12" md="2"><v-select v-model="form.postType" :items="postTypes" label="유형 *" variant="outlined" /></v-col>
        <v-col cols="12" md="2"><v-select v-model="form.priorityType" :items="priorities" label="중요도 *" variant="outlined" /></v-col>
        <v-col cols="12" md="3"><v-text-field v-model="form.dueDate" type="date" label="종료일" variant="outlined" /></v-col>
        <v-col cols="12" md="3"><v-text-field v-model="form.dueTime" type="time" label="종료시간" variant="outlined" /></v-col>
        <v-col cols="12"><v-text-field v-model="form.boardTitle" label="제목 *" variant="outlined" maxlength="200" /></v-col>
        <v-col cols="12" md="6"><v-select v-model="form.targetBranchCodes" :items="branchItems" label="대상 지점" variant="outlined" multiple chips clearable /></v-col>
        <v-col cols="12" md="6"><v-select v-model="form.targetAdminCodes" :items="adminItems" label="대상 담당자" variant="outlined" multiple chips clearable /></v-col>
        <v-col cols="12">
          <v-autocomplete
            v-model="form.referenceUserCode"
            :items="memberItems"
            :loading="memberLoading"
            label="참조 회원"
            placeholder="아이디, 이름, 전화번호로 검색"
            variant="outlined"
            clearable
            no-filter
            @update:search="searchMembers"
          />
        </v-col>
        <v-col cols="12">
          <div class="text-subtitle-2 font-weight-bold mb-2">내용 *</div>
          <RichTextEditor v-model="form.boardText" :upload-path="editorUploadPath" :min-height="420" />
        </v-col>
        <v-col cols="12">
          <div class="attachment-box">
            <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-3">
              <div>
                <div class="text-subtitle-2 font-weight-bold">첨부파일</div>
                <div class="text-caption text-grey">파일당 최대 100MB까지 추가할 수 있습니다.</div>
              </div>
              <input ref="attachmentInput" type="file" hidden @change="addAttachmentFile">
              <v-btn
                class="attachment-add-btn"
                variant="flat"
                prepend-icon="mdi-plus"
                :loading="uploading"
                @click="$refs.attachmentInput?.click()"
              >
                파일 추가
              </v-btn>
            </div>

            <div v-if="form.attachments.length || selectedFiles.length" class="attachment-list">
              <div
                v-for="(file, idx) in form.attachments"
                :key="file.fileCode || file.storageKey"
                class="attachment-item"
              >
                <v-icon size="18">mdi-paperclip</v-icon>
                <div class="attachment-name">
                  <div>{{ file.originalFileName }}</div>
                  <span>등록된 파일 · {{ formatFileSize(file.fileSize) }}</span>
                </div>
                <v-btn icon size="x-small" variant="text" color="error" @click="removeAttachment(idx)">
                  <v-icon size="18">mdi-close</v-icon>
                </v-btn>
              </div>

              <div
                v-for="(file, idx) in selectedFiles"
                :key="`${file.name}-${file.lastModified}-${idx}`"
                class="attachment-item"
              >
                <v-icon size="18">mdi-upload</v-icon>
                <div class="attachment-name">
                  <div>{{ file.name }}</div>
                  <span>저장 시 업로드 · {{ formatFileSize(file.size) }}</span>
                </div>
                <v-btn icon size="x-small" variant="text" color="error" @click="removeSelectedFile(idx)">
                  <v-icon size="18">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <div v-else class="attachment-empty">
              추가된 첨부파일이 없습니다.
            </div>
          </div>
        </v-col>
      </v-row>
      <div class="d-flex justify-end ga-2">
        <v-btn variant="text" to="/work-share">취소</v-btn>
        <v-btn color="primary" :loading="saving || uploading" @click="save">저장</v-btn>
      </div>
    </v-card>
  </div>
</template>

<style scoped>
.attachment-box {
  padding: 16px;
  border: 1px solid rgba(100, 116, 139, 0.42);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.34);
}

.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attachment-add-btn {
  min-width: 108px;
  border: 1px solid rgba(205, 178, 122, 0.82);
  background: #cdb27a !important;
  color: #111827 !important;
  font-weight: 700;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
}

.attachment-add-btn:hover {
  background: #dec58b !important;
}

.attachment-item {
  display: grid;
  grid-template-columns: 24px 1fr 32px;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid rgba(100, 116, 139, 0.36);
  border-radius: 8px;
  background: rgba(17, 24, 39, 0.54);
}

.attachment-name {
  min-width: 0;
}

.attachment-name div {
  overflow: hidden;
  color: #e5e7eb;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-name span,
.attachment-empty {
  color: #94a3b8;
  font-size: 12px;
}

.attachment-empty {
  padding: 14px;
  border: 1px dashed rgba(148, 163, 184, 0.36);
  border-radius: 8px;
  text-align: center;
}
</style>
